import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Collapse, IconButton, Paper, Tooltip } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import TuneIcon from "@mui/icons-material/Tune";
import api from "../../services/api";
import TaskViews from "../KeyComponents/TaskViews";
import TaskFilterControls from "../KeyComponents/TaskFilterControls";
import usePageViewMode from "../../utils/usePageViewMode";
import { DEFAULT_TABLE_COLUMNS } from "../../utils/tableColumns";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useView } from "../../utils/PersonalViewProvider";
import { useCustomViews } from "../../utils/CustomViewsProvider";
import useTaskFilterOptions from "../../utils/useTaskFilterOptions";

const ALL_VIEWS = ["list", "table", "kanban"];

// Renders a single CustomView (resolved by slug): editable filters + the
// List/Table/Kanban renderer limited to the view's enabled renderers.
const CustomViewPage = () => {
  const { slug } = useParams();
  const { views, loading: viewsLoading, updateView } = useCustomViews();
  const { currentWorkspace } = useWorkspace();
  const { selectedAssignee } = useView();
  const { sprintOptions, projectOptions, epicOptions, trackOptions } =
    useTaskFilterOptions(currentWorkspace?.id);

  const view = useMemo(
    () => views.find((v) => v.slug === slug),
    [views, slug],
  );

  const [filters, setFilters] = useState(null);
  const [sortCriteria, setSortCriteria] = useState([]);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_TABLE_COLUMNS);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const listViewRef = useRef();

  const enabledViews = useMemo(() => {
    const v = view?.config?.views || {};
    const list = ALL_VIEWS.filter((k) => v[k]);
    return list.length > 0 ? list : ["list"];
  }, [view]);

  const pageId = `custom-view-${slug}`;
  const [viewMode, setViewMode] = usePageViewMode(
    pageId,
    view?.config?.defaultView || enabledViews[0],
  );
  // Never render a view the user disabled (e.g. stale localStorage choice).
  const effectiveViewMode = enabledViews.includes(viewMode)
    ? viewMode
    : enabledViews[0];

  // Seed local editable state from the saved config whenever the view changes.
  useEffect(() => {
    if (!view) return;
    const cfg = view.config || {};
    setFilters({ ...(cfg.filters || {}) });
    setSortCriteria(cfg.sort || []);
    setVisibleCols({ ...DEFAULT_TABLE_COLUMNS, ...(cfg.columns || {}) });
    setDirty(false);
  }, [view]);

  const fetchProjects = useMemo(
    () => async () => {
      if (!currentWorkspace?.id || !filters) return [];
      try {
        const params = { workspace_id: currentWorkspace.id };
        if (selectedAssignee) {
          params.assignee = selectedAssignee;
        }
        // `taskType` is the pre-taxonomy-split key: the control has always
        // held Class values, so saved views still carrying it map to task_class.
        const taskClass = filters.taskClass || filters.taskType;
        if (taskClass) params.task_class = taskClass;
        if (filters.doDateFrom) params.do_date_from = filters.doDateFrom;
        if (filters.doDateTo) params.do_date_to = filters.doDateTo;
        if (filters.sprint) params.sprint_slot = filters.sprint;
        if (filters.status?.length) {
          params.status = filters.status.join(",");
          params.status_operator = filters.statusOperator || "contains";
        }
        if (filters.project) params.project = filters.project;
        if (filters.epic) params.primary_epic = filters.epic;
        if (filters.track) params.track = filters.track;
        if (effectiveViewMode === "table") params.table = "true";

        const res = await api.get("custom-view-tasks/", { params });
        return res.data?.payload || [];
      } catch (error) {
        console.error("Error fetching custom view tasks:", error);
        return [];
      }
    },
    [currentWorkspace, filters, effectiveViewMode, selectedAssignee],
  );

  useEffect(() => {
    if (!filters) return;
    setLoading(true);
    fetchProjects()
      .then(setProjects)
      .finally(() => setLoading(false));
  }, [fetchProjects, filters]);

  const handleFiltersChange = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const handleSortChange = (next) => {
    setSortCriteria((prev) => (typeof next === "function" ? next(prev) : next));
    setDirty(true);
  };
  const handleColsChange = (next) => {
    setVisibleCols((prev) => (typeof next === "function" ? next(prev) : next));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!view) return;
    try {
      await updateView(view.id, {
        config: {
          ...view.config,
          filters,
          sort: sortCriteria,
          columns: visibleCols,
        },
      });
      setDirty(false);
    } catch (error) {
      console.error("Error saving view:", error);
    }
  };

  if (viewsLoading && !view) return <div>Loading...</div>;
  if (!view) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">This personalized view no longer exists.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
        <Box />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton onClick={() => setShowFilters((v) => !v)} color={showFilters ? "primary" : "default"}>
              <TuneIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!dirty}
          >
            Save
          </Button>
        </Box>
      </Box>

      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, backgroundColor: "action.hover" }}>
          {filters && (
            <TaskFilterControls
              filters={filters}
              onChange={handleFiltersChange}
              sprintOptions={sprintOptions}
              projectOptions={projectOptions}
              epicOptions={epicOptions}
              trackOptions={trackOptions}
            />
          )}
        </Paper>
      </Collapse>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <TaskViews
          projects={projects}
          fetchProjects={fetchProjects}
          setProjects={setProjects}
          pageId={pageId}
          listViewRef={listViewRef}
          viewMode={effectiveViewMode}
          setViewMode={setViewMode}
          sortCriteria={sortCriteria}
          setSortCriteria={handleSortChange}
          visibleCols={visibleCols}
          setVisibleCols={handleColsChange}
          availableViews={enabledViews}
          emptyText="No tasks match this view's filters."
        />
      )}
    </Box>
  );
};

export default CustomViewPage;
